'use client';

import { useState } from 'react';
import { Mail, Phone, MapPin, Send } from 'lucide-react';

export default function ContactPage() {
  const [formSubmitted, setFormSubmitted] = useState(false);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Mock form submission
    setFormSubmitted(true);
    setTimeout(() => setFormSubmitted(false), 5000);
  };

  return (
    <div className="min-h-screen py-16">
      <div className="container mx-auto px-4">
        <div className="max-w-5xl mx-auto">
          {/* Header */}
          <div className="text-center mb-12">
            <h1 className="text-4xl md:text-5xl font-bold text-foreground mb-4">Get In Touch</h1>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Have questions about a trip? Want to discuss a custom adventure? I'd love to hear
              from you.
            </p>
          </div>

          {/* Custom Trip Planning Callout */}
          <div className="bg-gradient-to-r from-amber-50 to-orange-50 border border-terracotta/20 rounded-xl p-6 mb-12">
            <h3 className="text-xl font-bold text-gray-900 mb-2">
              🗺️ Planning a Custom Adventure?
            </h3>
            <p className="text-gray-700">
              I specialize in creating personalized backpacking experiences tailored to your group's goals,
              fitness level, and interests. Whether you're dreaming of a specific location, need flexible
              dates, or want to combine multiple activities, let's design the perfect trip together.
              Select "Custom Adventure Request" in the form below and tell me about your vision!
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-12">
            {/* Contact Information */}
            <div>
              <h2 className="text-2xl font-bold text-foreground mb-6">Contact Information</h2>
              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <Mail className="w-6 h-6 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-foreground mb-1">Email</h3>
                    <a
                      href="mailto:contact@backcountryadv.com"
                      className="text-muted-foreground hover:text-primary transition-colors"
                    >
                      contact@backcountryadv.com
                    </a>
                    <p className="text-sm text-muted-foreground mt-1">
                      I typically respond within 24 hours
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-full bg-secondary/10 flex items-center justify-center flex-shrink-0">
                    <Phone className="w-6 h-6 text-secondary" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-foreground mb-1">Phone</h3>
                    <a
                      href="tel:+14805551234"
                      className="text-muted-foreground hover:text-secondary transition-colors"
                    >
                      +1 (480) 555-1234
                    </a>
                    <p className="text-sm text-muted-foreground mt-1">
                      Available Mon-Fri, 9am-6pm MST
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-full bg-accent/10 flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-6 h-6 text-accent" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-foreground mb-1">Location</h3>
                    <p className="text-muted-foreground">Phoenix, Arizona</p>
                    <p className="text-sm text-muted-foreground mt-1">
                      Guiding trips across AZ, AK, PNW, and beyond
                    </p>
                  </div>
                </div>
              </div>

              {/* FAQ Quick Links */}
              <div className="mt-12 p-6 bg-card rounded-xl border border-border">
                <h3 className="font-semibold text-foreground mb-4">Quick Questions?</h3>
                <ul className="space-y-2 text-sm">
                  <li>
                    <a href="/gear-guide" className="text-primary hover:underline">
                      What gear do I need to bring?
                    </a>
                  </li>
                  <li>
                    <a href="/about" className="text-primary hover:underline">
                      What are Shane's qualifications?
                    </a>
                  </li>
                  <li>
                    <a href="/trips" className="text-primary hover:underline">
                      What difficulty level is right for me?
                    </a>
                  </li>
                  <li>
                    <a href="/booking" className="text-primary hover:underline">
                      How does the booking deposit work?
                    </a>
                  </li>
                </ul>
              </div>
            </div>

            {/* Contact Form */}
            <div>
              <h2 className="text-2xl font-bold text-foreground mb-6">Send a Message</h2>
              {formSubmitted ? (
                <div className="bg-secondary/10 border border-secondary/30 rounded-xl p-8 text-center">
                  <div className="w-16 h-16 rounded-full bg-secondary/20 flex items-center justify-center mx-auto mb-4">
                    <Send className="w-8 h-8 text-secondary" />
                  </div>
                  <h3 className="text-xl font-semibold text-foreground mb-2">Message Sent!</h3>
                  <p className="text-muted-foreground">
                    Thanks for reaching out. I'll get back to you as soon as possible.
                  </p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">
                      Your Name *
                    </label>
                    <input
                      type="text"
                      required
                      className="w-full px-4 py-3 rounded-lg border border-input bg-background text-foreground focus:ring-2 focus:ring-primary focus:border-transparent transition-all"
                      placeholder="John Doe"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">
                      Email Address *
                    </label>
                    <input
                      type="email"
                      required
                      className="w-full px-4 py-3 rounded-lg border border-input bg-background text-foreground focus:ring-2 focus:ring-primary focus:border-transparent transition-all"
                      placeholder="john@example.com"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">
                      Phone Number
                    </label>
                    <input
                      type="tel"
                      className="w-full px-4 py-3 rounded-lg border border-input bg-background text-foreground focus:ring-2 focus:ring-primary focus:border-transparent transition-all"
                      placeholder="(480) 555-1234"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">
                      Subject *
                    </label>
                    <select
                      required
                      className="w-full px-4 py-3 rounded-lg border border-input bg-background text-foreground focus:ring-2 focus:ring-primary focus:border-transparent transition-all"
                    >
                      <option value="">Select a subject</option>
                      <option>General Inquiry</option>
                      <option>Trip Question</option>
                      <option>Booking Support</option>
                      <option>Custom Adventure Request</option>
                      <option>Gear Questions</option>
                      <option>Other</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">
                      Message *
                    </label>
                    <textarea
                      required
                      rows={6}
                      className="w-full px-4 py-3 rounded-lg border border-input bg-background text-foreground focus:ring-2 focus:ring-primary focus:border-transparent transition-all resize-none"
                      placeholder="Tell me about your adventure goals, questions, or anything else you'd like to discuss..."
                    />
                  </div>

                  <button
                    type="submit"
                    className="w-full px-6 py-4 bg-primary text-primary-foreground rounded-xl hover:bg-primary/90 transition-all hover:shadow-lg font-semibold text-lg flex items-center justify-center gap-2"
                  >
                    <Send className="w-5 h-5" />
                    Send Message
                  </button>

                  <p className="text-sm text-muted-foreground text-center">
                    * This is a demo form. In production, messages would be sent to Shane's email.
                  </p>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
